import {
	HeadContent,
	Scripts,
	createRootRouteWithContext,
	retainSearchParams,
	stripSearchParams,
} from '@tanstack/react-router';
import * as v from 'valibot';
import appCssUrl from '../app.css?url';
import type { QueryClient } from '@tanstack/react-query';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { lazy, Suspense, useEffect, useState } from 'react';
import { onToasterActivated, flushPendingToasts } from '@/lib/toast-signal';
import { DefaultCatchBoundary } from './-components/default-catch-boundary';
import { NotFound } from './-components/not-found';

const Toaster = lazy(() => import('@worldcoo/ui/components/sonner').then((m) => ({ default: m.Toaster })));

export interface MyRouterContext {
	queryClient: QueryClient;
	baseUrl: string;
	portalId: string;
	locale: PortalLocale;
	isBot: boolean;
	isInIframe: boolean;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		lang: v.fallback(
			v.optional(
				v.picklist([
					'ca',
					'ca-ES',
					'de',
					'de-DE',
					'en',
					'en-EN',
					'en-US',
					'en-GB',
					'es',
					'es-ES',
					'eu',
					'eu-ES',
					'fr',
					'fr-FR',
					'gl',
					'gl-ES',
					'it',
					'it-IT',
					'nl',
					'nl-NL',
					'pt',
					'pt-PT',
					'pt-BR',
				]),
			),
			undefined,
		),
		iframe: v.fallback(v.optional(v.literal(true)), undefined),
		city: v.fallback(v.optional(v.string()), undefined),
	}),
	search: {
		// TODO: retainSearchParams is not working correctly with default values and stripSearchParams,
		// so we're passing search={(prev) => prev} manually in the links for now
		middlewares: [stripSearchParams({ q: '' }), retainSearchParams(['lang', 'iframe', 'city'])],
	},
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
		],
		links: import.meta.env.VITE_ANALYTICS_CODE
			? [
					{ rel: 'preconnect', href: 'https://www.googletagmanager.com' },
					{ rel: 'dns-prefetch', href: 'https://www.googletagmanager.com' },
				]
			: [],
		scripts: import.meta.env.VITE_ANALYTICS_CODE
			? [
					{
						src: `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_ANALYTICS_CODE}`,
						defer: true,
					},
				]
			: [],
	}),
	errorComponent: (props) => (
		<I18nProvider i18n={i18n}>
			<DefaultCatchBoundary {...props} />
		</I18nProvider>
	),
	notFoundComponent: () => (
		<I18nProvider i18n={i18n}>
			<NotFound />
		</I18nProvider>
	),
	shellComponent: RootDocument,
});

function OnDemandToaster() {
	const [show, setShow] = useState(false);
	useEffect(() => onToasterActivated(() => setShow(true)), []);
	if (!show) return null;
	return (
		<Suspense>
			<ToasterWithFlush />
		</Suspense>
	);
}

function ToasterWithFlush() {
	useEffect(() => {
		flushPendingToasts();
	}, []);
	return (
		<Toaster
			className="whitespace-pre-line print:hidden"
			offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
			mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
			position="top-center"
			theme="light"
		/>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale } = Route.useRouteContext();
	const cdnUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_CDN_URL ?? '');

	useEffect(() => {
		if (import.meta.env.DEV) {
			void import('react-grab');
		}
	}, []);

	return (
		<html lang={locale}>
			<head>
				<HeadContent />

				{cdnUrl && (
					<>
						<link rel="preconnect" href={cdnUrl} />
						<link rel="dns-prefetch" href={cdnUrl} />
					</>
				)}
				{import.meta.env.VITE_IMGPROXY_URL && (
					<>
						<link rel="preconnect" href={import.meta.env.VITE_IMGPROXY_URL} />
						<link rel="dns-prefetch" href={import.meta.env.VITE_IMGPROXY_URL} />
					</>
				)}
				<link rel="preload" href={appCssUrl} as="style" />
				<link rel="stylesheet" href={appCssUrl} />
				<link
					rel="preload"
					href={`${cdnUrl}/rethink-sans-variable.woff2`}
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<style>
					{'@font-face {' +
						"font-family: 'Rethink Sans Variable';" +
						'font-style: normal;' +
						'font-display: swap;' +
						'font-weight: 300 800;' +
						`src: url('${cdnUrl}/rethink-sans-variable.woff2') format('woff2');` +
						'}'}
				</style>
			</head>

			<body className="min-h-dvh overflow-y-scroll">
				{children}
				<OnDemandToaster />

				<div className="hidden sm:block">
					<TanStackDevtools
						plugins={[
							{
								name: 'Tanstack Router',
								render: <TanStackRouterDevtoolsPanel />,
							},
							{
								name: 'Tanstack Query',
								render: <ReactQueryDevtoolsPanel />,
							},
							{
								name: 'Tanstack Form',
								render: <FormDevtoolsPanel />,
							},
						]}
					/>
				</div>
				<Scripts />
			</body>
		</html>
	);
}
